import React from 'react';
import { z } from 'zod';

import { ColDefBuilder, DataTable, Heading } from '@eluve/components';
import { pfshSchema } from '@eluve/llm-outputs';

import { OutputParsingError } from './OutputParsingError';
import { OutputTitle } from './OutputTitle';

export interface PfshOutputProps {
  content: unknown;
}

type MedicationRow = z.infer<typeof pfshSchema>['medications'][number];

const columns = new ColDefBuilder<MedicationRow>()
  .defaultSortable('name')
  .defaultSortable('dose')
  .defaultSortable('frequency')
  .build();

export const PfshOutput: React.FC<PfshOutputProps> = ({ content }) => {
  const pfsh = pfshSchema.safeParse(content);

  if (!pfsh.success) {
    return <OutputParsingError />;
  }

  return (
    <div className="space-y-4">
      <OutputTitle title="Past, Family and Social History (PFSH)" />

      <div className="space-y-2">
        <Heading size="2xl" weight="semibold">
          Allergies
        </Heading>
        <ul>
          {pfsh.data.allergies.map((allergy) => (
            <li key={allergy}>{allergy}</li>
          ))}
        </ul>
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          Medications
        </Heading>
        <DataTable
          columns={columns}
          data={pfsh.data.medications}
          isPaginated={false}
        />
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          Existing Conditions
        </Heading>
        <ul className="list-inside list-disc">
          {pfsh.data.existingConditions.map((condition) => (
            <li key={condition}>{condition}</li>
          ))}
        </ul>
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          Surgeries
        </Heading>
        <ul className="list-inside list-disc">
          {pfsh.data.surgeries.map((surgery) => (
            <li key={surgery}>{surgery}</li>
          ))}
        </ul>
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          Social History
        </Heading>
        <ul className="list-inside list-disc">
          {pfsh.data.socialHistory.map((social) => (
            <li key={social}>{social}</li>
          ))}
        </ul>
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          Family History
        </Heading>
        <ul className="list-inside list-disc">
          {pfsh.data.familyHistory.map((family) => (
            <li key={family}>{family}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};
